import { useState, useRef, useEffect } from 'react'

function useMediaCarousel({
  useCaseId,
  initialMediaIndex,
  embedsCount,
  isGalleryView,
  keydownTargetRef,
}) {
  const [currentIndex, setCurrentIndex] = useState(initialMediaIndex || 0)
  const useCaseIdRef = useRef(useCaseId)

  // Reset index when use case changes
  if (useCaseId !== useCaseIdRef.current) {
    useCaseIdRef.current = useCaseId
    setCurrentIndex(initialMediaIndex || 0)
  }

  // Reset index when initialMediaIndex changes
  useEffect(() => {
    if (initialMediaIndex !== undefined) {
      setCurrentIndex(initialMediaIndex)
    }
  }, [initialMediaIndex])

  // Handle arrow key navigation (only in gallery view)
  useEffect(() => {
    if (!isGalleryView) {
      return undefined
    }

    const navTarget = keydownTargetRef?.current

    if (!navTarget) {
      return undefined
    }

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        event.stopPropagation()
        setCurrentIndex(prev => Math.max(0, prev - 1))
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        event.stopPropagation()
        const maxIndex = embedsCount - 1
        setCurrentIndex(prev => Math.min(maxIndex, prev + 1))
      }
    }

    navTarget.addEventListener('keydown', handleKeyDown)

    return () => {
      navTarget.removeEventListener('keydown', handleKeyDown)
    }
  }, [embedsCount, isGalleryView, keydownTargetRef])

  const handlePrevious = (event) => {
    event.stopPropagation()
    setCurrentIndex(prev => Math.max(0, prev - 1))
  }

  const handleNext = (event) => {
    event.stopPropagation()
    const maxIndex = embedsCount - 1
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1))
  }

  const handleDotClick = (event, index) => {
    event.stopPropagation()
    setCurrentIndex(index)
  }

  return {
    currentIndex,
    handlePrevious,
    handleNext,
    handleDotClick,
  }
}

export default useMediaCarousel
